/**
 * @module CreateCustomerApiService
 * @remarks RTK Query service for customer registration
 */

import { CreateCustomerResponse, LoginResponse, RegisterResponse, RegistrationData } from '@models/auth.model';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { protectCityNameForDatabase } from '@/helpers/addressHelpers';

export const createCustomerApi = createApi({
  reducerPath: 'createCustomerApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/',
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        headers.set('Authorization', token);
      }
      return headers;
    }
  }),
  tagTypes: ['NewCustomer'],
  endpoints: (builder) => ({
    // Register a new user and create a customer profile
    registerCustomer: builder.mutation<CreateCustomerResponse, RegistrationData>({
      async queryFn(registrationData, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          // Schritt 0: Überprüfen, ob die E-Mail bereits existiert
          const checkEmailResult = await fetchWithBQ({
            url: 'auth?path=auth/checkMail',
            method: 'POST',
            body: {
              mail: registrationData.email
            }
          });
          
          if (checkEmailResult.error) {
            return { error: checkEmailResult.error };
          }
          
          const emailCheckData = checkEmailResult.data as { exists: string };
          if (emailCheckData.exists === "true") {
            return { 
              error: { 
                status: 'CUSTOM_ERROR', 
                error: 'Email already exists',
                data: { code: 'EMAIL_EXISTS' }
              } 
            };
          }

          // Schritt 1: Benutzer registrieren
          const registerResult = await fetchWithBQ({
            url: 'auth?path=auth/register',
            method: 'POST',
            body: {
              mail: registrationData.email,
              password: registrationData.password
            }
          });
          
          if (registerResult.error) {
            return { error: registerResult.error };
          }
          
          const registerData = registerResult.data as RegisterResponse;
          if (registerData.message !== "register successful") {
            return { error: { status: 'CUSTOM_ERROR', error: 'Registration failed' } };
          }
          
          // Step 2: Login with the newly created credentials
          const loginResult = await fetchWithBQ({
            url: 'auth?path=auth/login',
            method: 'POST',
            body: {
              mail: registrationData.email,
              password: registrationData.password
            }
          });
          
          if (loginResult.error) {
            return { error: loginResult.error };
          }
          
          const loginData = loginResult.data as LoginResponse;
          
          // Save JWT token to localStorage
          localStorage.setItem("auth_token", loginData.jwt);
          
          // Step 3: Create the customer profile
          const createCustomerResult = await fetchWithBQ({
            url: 'data/kunden',
            method: 'PUT',
            headers: {
              Authorization: loginData.jwt
            },
            body: {
              where: {
                users: loginData.user_id
              },
              fields: {
                anrede: registrationData.anrede,
                vorname: registrationData.vorname,
                nachname: registrationData.nachname,
                e_mail: registrationData.email,
                telefon: registrationData.telefon,
                strasse_und_hausnummer: registrationData.streetAndHouseNumber,
                adresszusatz: registrationData.adresszusatz,
                plz: registrationData.plz,
                ort: protectCityNameForDatabase(registrationData.ort),
                country: registrationData.country,
                geburtsdatum: registrationData.geburtsdatum,
                teilnahmebedingungen: 1,
                datenschutz: 1,
                marketing: registrationData.marketing,
                users: loginData.user_id,
                erstelldatum: new Date().toISOString(),
                trigger_create_card: registrationData.trigger_create_card,
                brand_create_card: registrationData.brand_create_card,
              }
            }
          });
          
          if (createCustomerResult.error) {
            return { error: createCustomerResult.error };
          }
          
          // Schritt 4: Kontakt synchronisieren
          const syncContactResult = await fetchWithBQ({
            url: 'khg/sync/createContact',
            method: 'POST',
            headers: {
              Authorization: loginData.jwt
            },
            body: {
              id: loginData.user_id
            }
          });
          if (syncContactResult.error) {
            console.error('Fehler bei der Kontaktsynchronisierung:', syncContactResult.error);
          }
          
          return { data: { 
            success: true, 
            data: {
              user_id: loginData.user_id,
              jwt: loginData.jwt
            }
          }};
        } catch (error) {
          return { 
            error: { 
              status: 'CUSTOM_ERROR', 
              error: `An unexpected error occurred: ${error}` 
            } 
          };
        }
      }
    })
  })
});

// Export hooks for use in components
export const {
  useRegisterCustomerMutation
} = createCustomerApi;
